import React, { useCallback, useEffect, useState } from "react";
import { Topbar, CalendarModal, EmptyCard } from "@components";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { Radio, Spin } from "antd";
import {
  AmountText,
  BonusStatusText,
  DateText,
  ExpiredContainer,
  ExpiredText,
  Image,
  ItemText,
  ListItem,
  SpinWrapper,
  StatusText,
  Wrapper,
} from "../Wallet/Wallet.style";
import { ListWrapper } from "../Wallet/Wallet.style";
import {
  CalendarFilled,
  UploadFill,
  AddCircleFill,
  BonusFill,
  BlackUploadFill,
  BlackAddCircleFill,
  BlackBonusFill,
} from "@assets";
import { api } from "@api";
import { LIMITS, TOPUP_SK, WITHDRAW_SK, BONUS_SK } from "@constants";
import InfiniteScroll from "react-infinite-scroll-component";
import { getFormattedNumber } from "@utils";
import moment from "moment";
import { BonusImage } from "../Wallet/Wallet";
import { useTranslation } from "react-i18next";
import { getStatus, getUseOrNotUse } from "@utils";
import { PRIVATE_ROUTE } from "@router/route.constants";

const RootContainer = styled.div`
  padding: 24px 16px;
  height: calc(100vh - 118px);
  overflow: hidden;

  .ant-radio-wrapper {
    padding-bottom: 10px;
    margin-right: 0px;
  }

  .anticon .anticon-calendar {
    font-size: 26px;
    color: #fff;
  }
`;

const StyledListWrapper = styled(ListWrapper).attrs({
  id: "scrollable-div",
})`
  height: calc(100vh - 140px);
  padding-top: 0 !important;
  background: ${(props) => props.theme.white};
  border-radius: 12px;
`;

const RadioGroup = styled(Radio.Group)`
  display: flex;
  align-items: center;
  .ant-radio-wrapper span.ant-radio + * {
    padding-inline-start: 0px !important;
  }
`;

export const RadioButton = styled(Radio)`
  .ant-radio {
    opacity: 0;
    position: fixed;
    width: 0;
  }
`;

export const Text = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${(props) => props.theme.textColor};
  background: ${(props) => (props.status ? props.theme.golden : "#ffffff")};
  padding: 5px 10px 3px 10px;
  border-radius: 23px;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 20px;
  min-width: 30px;
  svg {
    padding-right: 5px;
  }

  @media screen and (max-width: 445px) {
    font-size: 10px;
    padding: 5px 8px 3px 8px;
  }

  @media screen and (max-width: 355px) {
    font-size: 9px;
    padding: 5px 6px 3px 6px;
    svg {
      padding-right: 3px;
      width: 17px;
      height: 17px;
    }
  }
`;

const { DEFAULT_PAGE, PAGE_SIZE } = LIMITS;

const dateFormat = "MMM DD, YYYY hh:mm:ss A";
const apiDateFormat = "YYYY-MM-DD";

const TransactionHistory = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [type, setType] = useState("");
  const [page, setPage] = useState(DEFAULT_PAGE);
  const [total, setTotal] = useState(0);
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState("");
  const [loading, setLoading] = useState(true);

  const getTransactions = useCallback(() => {
    setLoading(true);
    api
      .getTransactions({
        page,
        limit: PAGE_SIZE,
        type,
        start_date: date,
        end_date: date,
      })
      .then(({ transactions, pagination }) => {
        setData((prevState) => {
          return [...prevState, ...transactions];
        });
        setPage(pagination.page);
        setTotal(pagination.total);
        setLoading(false);
      })
      .catch(() => { });
  }, [type, page, date]);

  useEffect(() => {
    getTransactions();
  }, [getTransactions]);

  const onChange = (e) => {
    setType(e.target.value);
    setPage(DEFAULT_PAGE);
    setData([]);
    setTotal(0);
  };

  const onOk = (newdate) => {
    newdate = newdate.format(apiDateFormat);
    if (newdate !== date) {
      setDate(newdate);
      setType("");
      setPage(DEFAULT_PAGE);
      setData([]);
      setTotal(0);
    }
    handleClose();
  };

  const onReset = () => {
    setDate("");
    setType("");
    setPage(DEFAULT_PAGE);
    setData([]);
    setTotal(0);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const goBack = () => {
    navigate(`/${PRIVATE_ROUTE.WALLET}`);
  };

  const renderTop = () => (
    <RadioGroup onChange={onChange} value={type}>
      <RadioButton value={""}>
        <Text status={type === ""}>
          {t("wallet.transactionHistoryHeadingAll")}
        </Text>
      </RadioButton>
      <RadioButton value={WITHDRAW_SK}>
        <Text status={type === WITHDRAW_SK}>
          {type === WITHDRAW_SK ? <BlackUploadFill /> : <UploadFill />}
          {t("wallet.walletWithdraw")}
        </Text>
      </RadioButton>
      <RadioButton value={TOPUP_SK}>
        <Text status={type === TOPUP_SK}>
          {type === TOPUP_SK ? <BlackAddCircleFill /> : <AddCircleFill />}
          {t("wallet.walletTopUp")}
        </Text>
      </RadioButton>
      <RadioButton value={BONUS_SK}>
        <Text status={type === BONUS_SK}>
          {type === BONUS_SK ? <BlackBonusFill /> : <BonusFill />}
          {t("wallet.transactionHistoryHeadingBonus")}
        </Text>
      </RadioButton>
    </RadioGroup>
  );

  const Loader = () => (
    <SpinWrapper>
      <Spin />
    </SpinWrapper>
  );

  return (
    <>
      <Topbar
        title={t("wallet.transactionHistoryTitle")}
        rightIcon={<CalendarFilled onClick={() => setOpen(true)} />}
        goBack={goBack}
      />
      <RootContainer>
        {!loading && renderTop()}
        <StyledListWrapper>
          <InfiniteScroll
            dataLength={data.length}
            next={() => setPage((prev) => prev + 1)}
            hasMore={loading || data.length < total}
            loader={<Loader />}
            scrollableTarget="scrollable-div"
            endMessage={
              !loading && data.length === 0 && <EmptyCard minHeight={130} />
            }
          >
            {data.map((item, key) => (
              <ListItem key={key}>
                {item.type === BONUS_SK ? (
                  <BonusImage />
                ) : (
                  <Image
                    src={item.payment_category_image}
                    radius={
                      item.payment_category_name === "KBZ" ? "none" : "circle"
                    }
                  />
                )}
                <Wrapper>
                  <ItemText>
                    {item.type === BONUS_SK
                      ? t("wallet.transactionHistoryBonusType", {
                        bonus_type: item.bonus_type.replaceAll('-bonus', ''),
                      })
                      : `${item.type === WITHDRAW_SK
                        ? t("wallet.walletWithdraw")
                        : t("wallet.walletTopUp")
                      } ${t("common.to")} ${item.num}`}
                  </ItemText>
                  <DateText>
                    {moment.unix(item.created_at).format(dateFormat)}
                  </DateText>
                  {item.trx_num && (
                    <DateText>{`PIN: ${item.trx_num}`}</DateText>
                  )}
                  {item.type === BONUS_SK && item.bonus_expired && (
                    <ExpiredContainer>
                      <ExpiredText>{t("wallet.expiredOn")}</ExpiredText>
                      <DateText>
                        {moment.unix(item.bonus_expired).format(dateFormat)}
                      </DateText>
                    </ExpiredContainer>
                  )}
                </Wrapper>
                <Wrapper>
                  <AmountText>{`${getFormattedNumber(
                    item.amount
                  )} Ks.`}</AmountText>
                  {item.type !== BONUS_SK && (
                    <StatusText status={item.status}>
                      {getStatus(item.status)}
                    </StatusText>
                  )}
                  {item.type === BONUS_SK && item.bonus_expired && (
                    <BonusStatusText status={item.remaining_bonus === 0}>
                      {getUseOrNotUse(item.remaining_bonus === 0)}
                    </BonusStatusText>
                  )}
                </Wrapper>
              </ListItem>
            ))}
          </InfiniteScroll>
        </StyledListWrapper>
      </RootContainer>
      <CalendarModal
        open={open}
        date={date}
        onOk={onOk}
        onReset={onReset}
        handleClose={handleClose}
      />
    </>
  );
};

export default TransactionHistory;
